/**
 * `useCardEmbed` — Phase 4c hook for the wiki work-item embed NodeView.
 *
 * Fetches a single card via `GET /api/cards/:cardId` (new BE route
 * shipped in lockstep with this hook by the Phase 4c BE agent). The
 * route returns the minimum chip-render subset:
 *
 *   { id, projectId, kind, code, title, statusId, statusName, statusColor?,
 *     assigneeId, assigneeName, assigneeAvatarUrl }
 *
 * RLS-aware on the BE; the hook surfaces the three terminal states the
 * NodeView needs to render:
 *
 *   - `{ isLoading: true }`     — fetch in flight
 *   - `{ isRestricted: true }`  — 403 from `canViewCard`
 *   - `{ isDeleted: true }`     — 404 (card hard-deleted or never existed)
 *   - `{ data: CardEmbedPayload }` — happy path
 *
 * Realtime: subscribes to the shared WebSocket via
 * `useWebSocketSubscription` and invalidates `CARD_EMBED_QUERY_KEY` on
 * any `card.updated` / `card.deleted` frame whose payload id matches.
 * The board-realtime hook already cross-invalidates `['board', X]` on
 * the same frames; we add a parallel invalidate here so an embed in a
 * wiki page (which never opens `useBoard`) still ticks live.
 *
 * Cross-project safe: the work-item embed can reference a card in ANY
 * project the reader has access to. We don't filter the WS frame by
 * `projectId` — the embed's own `cardId` is the dedupe key.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { ApiError, apiClient } from '@/api/client'
import type { DeliverableKind } from '@/api/types-bridge'
import type { ServerMessage } from '@/lib/use-websocket'
import { useWebSocketSubscription } from '@/lib/use-websocket-subscription'

/** Stable cache key — one entry per card id. */
export const CARD_EMBED_QUERY_KEY = (cardId: string) => ['card-embed', cardId] as const

/**
 * Chip-render subset of a card — exactly what `?fields=chip` returns
 * and the NodeView renders. Kept inline here (rather than re-exported
 * from `types-bridge`) because the shape is wiki-specific.
 *
 * `status` is the nested object the BE actually emits. It was declared
 * as a flat `statusName` for a long time and the BE never sent that, so
 * the chip's status pill silently never rendered — don't reintroduce a
 * flat mirror of a nested wire field. (A `projectCode` field was
 * declared here for just as long and was never emitted or read either;
 * removed. Only declare what the route actually sends.)
 */
export interface CardEmbedPayload {
  readonly id: string
  readonly projectId: string
  readonly kind: DeliverableKind
  readonly code: string | null
  readonly title: string
  readonly statusId: string
  readonly status?: {
    readonly id: string
    readonly name: string
    readonly color: string | null
  } | null
  readonly assigneeId: string | null
  readonly assigneeName: string | null
  readonly assigneeAvatarUrl: string | null
}

export type CardEmbedState =
  | {
      readonly isLoading: true
      readonly data?: undefined
      readonly isRestricted?: false
      readonly isDeleted?: false
    }
  | {
      readonly isLoading: false
      readonly isRestricted: true
      readonly data?: undefined
      readonly isDeleted?: false
    }
  | {
      readonly isLoading: false
      readonly isDeleted: true
      readonly data?: undefined
      readonly isRestricted?: false
    }
  | {
      readonly isLoading: false
      readonly data: CardEmbedPayload
      readonly isRestricted?: false
      readonly isDeleted?: false
    }

interface CardEventPayload {
  id: string
  projectId: string | null
  kind: DeliverableKind
}

function isCardEventPayload(payload: unknown): payload is CardEventPayload {
  if (typeof payload !== 'object' || payload === null) return false
  const p = payload as Record<string, unknown>
  return typeof p.id === 'string' && typeof p.kind === 'string'
}

/**
 * One chip-shaped card read, shared by this hook and by the editor's
 * reference resolver (which needs it outside any component).
 *
 * `?fields=chip` asks the BE for the chip subset only — it skips the
 * satellite query batch (labels / attendees / collaborators / dates)
 * neither caller reads, and OMITS those fields from the payload. Deep
 * links keep the default `full` mode.
 *
 * `?kind=` is an OPTIONAL lookup hint the embed node persists at insert
 * time (the picker knows the kind of the card chosen). With it the BE
 * answers in one statement; without it — an older chip, a paste, a
 * hand-written node — it spends one more resolving which table owns the
 * id, and returns exactly the same payload. Never make this required:
 * the chip reads the authoritative `kind` off the RESPONSE, so for an
 * unhinted embed the kind is an output of this very fetch.
 *
 * It THROWS the `ApiError` rather than answering null on a miss, and
 * that is load-bearing for the hook below: 403 and 404 render as two
 * different chips (restricted vs deleted), and a `useQuery` whose
 * `queryFn` resolved to null would leave `isError` false and `data`
 * empty — the hook's own `!query.data` branch would then report
 * `isLoading` forever, i.e. a permanent skeleton on a deleted card.
 * Callers that only care whether the card exists catch it instead.
 */
export async function fetchCardEmbedById(
  cardId: string,
  kindHint?: DeliverableKind,
): Promise<CardEmbedPayload> {
  const hint = kindHint ? `&kind=${kindHint}` : ''
  const res = await apiClient<{ data: CardEmbedPayload }>(`/api/cards/${cardId}?fields=chip${hint}`)
  return res.data
}

/**
 * Wiki work-item embed cache + WS subscription. Disabled when
 * `cardId` is empty/undefined (placeholder state before the picker
 * fills the node attrs).
 */
export function useCardEmbed(
  cardId: string | null | undefined,
  kindHint?: DeliverableKind,
): CardEmbedState {
  const queryClient = useQueryClient()

  const query = useQuery<CardEmbedPayload>({
    // The hint deliberately does NOT participate in the cache key: a
    // hinted and an unhinted read of the same card return byte-identical
    // payloads, so keying on it would just fragment the cache.
    queryKey: CARD_EMBED_QUERY_KEY(cardId ?? ''),
    queryFn: () => fetchCardEmbedById(cardId ?? '', kindHint),
    enabled: Boolean(cardId),
    // The card chip is a small surface — let the WS invalidation drive
    // freshness and treat the cached value as fresh otherwise. Saves a
    // refetch storm when many embeds mount at once.
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    // 403 / 404 are terminal for the embed UI; don't retry them.
    retry: false,
  })

  // WS bridge — invalidate the per-card cache on every `card.updated` /
  // `card.deleted` frame whose payload id matches our cardId.
  //
  // We do NOT filter by `projectId` because a wiki embed can reference
  // a card in any project the reader can see; the per-card filter
  // (`payload.id === cardId`) is the dedupe.
  const handleMessage = useCallback(
    (message: ServerMessage) => {
      if (!cardId) return
      if (message.type !== 'card.updated' && message.type !== 'card.deleted') return
      const payload = message.payload
      if (!isCardEventPayload(payload)) return
      if (payload.id !== cardId) return
      queryClient.invalidateQueries({ queryKey: CARD_EMBED_QUERY_KEY(cardId) })
    },
    [cardId, queryClient],
  )

  useWebSocketSubscription(handleMessage)

  if (!cardId || query.isLoading) {
    return { isLoading: true }
  }

  if (query.isError) {
    const err = query.error
    if (err instanceof ApiError) {
      if (err.status === 403) return { isLoading: false, isRestricted: true }
      if (err.status === 404) return { isLoading: false, isDeleted: true }
    }
    // Unknown error — fall back to "deleted" so the UI degrades to a
    // grey-out chip rather than a flash of `<undefined>`. The actual
    // error is observable in the network tab + console.
    return { isLoading: false, isDeleted: true }
  }

  if (!query.data) {
    return { isLoading: true }
  }

  return { isLoading: false, data: query.data }
}
